import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import {
  ArrowLeft, Save, Send, Plus, Trash2, MapPin,
  FileText, Upload, Car, Receipt, Edit, AlertTriangle
} from 'lucide-react';
import { formatCurrencyAmount } from '../components/CurrencyDisplay';

export default function ExpenseEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Loading
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Expense header data
  const [expense, setExpense] = useState(null);
  const [expenseDate, setExpenseDate] = useState('');
  const [purpose, setPurpose] = useState('');
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [subTotal, setSubTotal] = useState('');
  const [vatAmount, setVatAmount] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [glAccountId, setGlAccountId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [costCentre, setCostCentre] = useState('');

  // Mileage data
  const [isMileage, setIsMileage] = useState(false);
  const [trips, setTrips] = useState([]);
  const [vehicleType, setVehicleType] = useState('Car');

  // Trip edit form
  const [showTripForm, setShowTripForm] = useState(false);
  const [editingTripIndex, setEditingTripIndex] = useState(null);
  const [tripDate, setTripDate] = useState(new Date().toISOString().split('T')[0]);
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [distance, setDistance] = useState('');
  const [tripPurpose, setTripPurpose] = useState('');
  const [returnTrip, setReturnTrip] = useState(false);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploadQueue, setUploadQueue] = useState([]);

  // Reference data
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    loadAll();
  }, [id]);

  useEffect(() => {
    if (!isMileage) {
      const sub = parseFloat(subTotal) || 0;
      const vat = parseFloat(vatAmount) || 0;
      setTotalAmount((sub + vat).toFixed(2));
    }
  }, [subTotal, vatAmount, isMileage]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [expRes, catRes, deptRes, glRes, projRes, currRes] = await Promise.all([
        apiClient.get(`/expenses/${id}`),
        apiClient.get('/expenses/categories/list'),
        apiClient.get('/departments'),
        apiClient.get('/gl-accounts'),
        apiClient.get('/projects'),
        apiClient.get('/currencies')
      ]);

      // Reference data
      setCategories(catRes.data.categories || []);
      setDepartments(deptRes.data.departments || []);
      setGlAccounts(glRes.data.glAccounts || glRes.data || []);
      setProjects(projRes.data.projects || projRes.data || []);
      setCurrencies(currRes.data.currencies || currRes.data || []);

      const exp = expRes.data.expense;
      if (!exp) {
        alert('Expense not found');
        navigate('/expenses');
        return;
      }

      const editableStatuses = ['Draft', 'Rejected', 'Pending', 'Pending Approval'];
      if (!editableStatuses.includes(exp.Status)) {
        alert('This expense can no longer be edited');
        navigate(`/expenses/${id}`);
        return;
      }

      setExpense(exp);

      // Populate form
      setExpenseDate(exp.ExpenseDate ? new Date(exp.ExpenseDate).toISOString().split('T')[0] : '');
      setPurpose(exp.Purpose || '');
      setCategory(exp.Category || '');
      setNotes(exp.Notes || '');
      setCurrency(exp.Currency || 'GBP');
      setSubTotal(exp.SubTotal?.toString() || '');
      setVatAmount(exp.VATAmount?.toString() || '');
      setTotalAmount(exp.TotalAmount?.toString() || '');
      setDepartmentId(exp.DepartmentID || '');
      setGlAccountId(exp.GLAccountID || '');
      setProjectId(exp.ProjectID || '');
      setCostCentre(exp.CostCentre || '');

      // Documents
      setDocuments(expRes.data.documents || []);

      // Check if mileage
      const mileage = exp.Category === 'Mileage';
      setIsMileage(mileage);

      if (mileage) {
        try {
          const tripsRes = await apiClient.get(`/expenses/${id}/mileage-trips`);
          const dbTrips = (tripsRes.data.trips || []).map((t, idx) => ({
            id: t.ExpenseLineID || `trip-${idx}`,
            dbId: t.ExpenseLineID,
            date: t.ExpenseDate ? new Date(t.ExpenseDate).toISOString().split('T')[0] : '',
            fromLocation: t.MileageFromLocation || '',
            toLocation: t.MileageToLocation || '',
            distance: parseFloat(t.MileageDistance) || 0,
            purpose: t.MileagePurpose || t.Description || '',
            returnTrip: false,
            ratePerMile: parseFloat(t.MileageRatePerMile) || 0.55,
            amount: parseFloat(t.GrossAmount) || 0,
            vehicleType: t.MileageVehicleType || 'Car'
          }));
          setTrips(dbTrips);
          if (dbTrips.length > 0 && dbTrips[0].vehicleType) {
            setVehicleType(dbTrips[0].vehicleType);
          }
        } catch (err) {
          console.error('Failed to load trips:', err);
        }
      }
    } catch (error) {
      console.error('Failed to load expense:', error);
      alert('Failed to load expense');
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  // ==================== TRIP HANDLERS ====================
  const handleAddTrip = () => {
    if (!fromLocation || !toLocation || !distance || !tripPurpose) {
      alert('Please fill in all trip details');
      return;
    }

    const totalDistance = parseFloat(distance) * (returnTrip ? 2 : 1);
    const newTrip = {
      id: Date.now(),
      date: tripDate,
      fromLocation,
      toLocation,
      distance: totalDistance,
      purpose: tripPurpose,
      returnTrip,
      ratePerMile: 0.55,
      amount: totalDistance * 0.55,
      isNew: true
    };

    if (editingTripIndex !== null) {
      const updated = [...trips];
      updated[editingTripIndex] = { ...updated[editingTripIndex], ...newTrip, id: updated[editingTripIndex].id };
      setTrips(updated);
      setEditingTripIndex(null);
    } else {
      setTrips([...trips, newTrip]);
    }

    resetTripForm();
  };

  const handleEditTrip = (index) => {
    const trip = trips[index];
    setFromLocation(trip.fromLocation);
    setToLocation(trip.toLocation);
    setDistance(trip.returnTrip ? trip.distance / 2 : trip.distance);
    setTripPurpose(trip.purpose);
    setReturnTrip(trip.returnTrip);
    setTripDate(trip.date);
    setEditingTripIndex(index);
    setShowTripForm(true);
  };

  const handleDeleteTrip = (index) => {
    if (window.confirm('Delete this trip?')) {
      setTrips(trips.filter((_, i) => i !== index));
    }
  };

  const resetTripForm = () => {
    setFromLocation('');
    setToLocation('');
    setDistance('');
    setTripPurpose('');
    setReturnTrip(false);
    setTripDate(new Date().toISOString().split('T')[0]);
    setEditingTripIndex(null);
    setShowTripForm(false);
  };

  const getTotalMiles = () => trips.reduce((sum, t) => sum + t.distance, 0);
  const getTotalMileageAmount = () => trips.reduce((sum, t) => sum + t.amount, 0);

  // ==================== FILE HANDLERS ====================
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      setUploadQueue(prev => [...prev, {
        file,
        name: file.name,
        size: file.size,
        uploading: false,
        uploaded: false
      }]);
    }
    e.target.value = '';
  };

  const handleRemoveNewDoc = (index) => {
    setUploadQueue(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPendingDocuments = async (expenseId) => {
    for (let i = 0; i < uploadQueue.length; i++) {
      const doc = uploadQueue[i];
      if (doc.uploaded) continue;

      try {
        setUploadQueue(prev => prev.map((d, idx) =>
          idx === i ? { ...d, uploading: true } : d
        ));

        const formData = new FormData();
        formData.append('file', doc.file);
        formData.append('expenseId', expenseId);
        formData.append('documentType', 'Receipt');

        await apiClient.post('/expenses/documents', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setUploadQueue(prev => prev.map((d, idx) =>
          idx === i ? { ...d, uploading: false, uploaded: true } : d
        ));
      } catch (error) {
        console.error(`Failed to upload ${doc.name}:`, error);
        setUploadQueue(prev => prev.map((d, idx) =>
          idx === i ? { ...d, uploading: false } : d
        ));
      }
    }
  };

  // ==================== SAVE / SUBMIT ====================
  const handleSave = async () => {
    if (!purpose.trim()) {
      alert('Please enter a purpose');
      return;
    }
    if (!departmentId) {
      alert('Please select a department');
      return;
    }

    setSaving(true);
    try {
      await apiClient.put(`/expenses/${id}`, {
        expenseDate,
        currency,
        subTotal: parseFloat(subTotal) || 0,
        vATAmount: parseFloat(vatAmount) || 0,
        totalAmount: parseFloat(totalAmount) || 0,
        departmentId,
        gLAccountId: glAccountId || null,
        projectId: projectId || null,
        costCentre: costCentre || null,
        purpose,
        category: isMileage ? 'Mileage' : category,
        notes
      });

      if (uploadQueue.length > 0) {
        await uploadPendingDocuments(id);
      }

      alert('Expense updated successfully');
      navigate(`/expenses/${id}`);
    } catch (error) {
      console.error('Save error:', error);
      alert(error.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!purpose.trim()) {
      alert('Please enter a purpose');
      return;
    }
    if (!departmentId) {
      alert('Please select a department');
      return;
    }

    if (!window.confirm('Submit this expense for approval?')) return;

    setSubmitting(true);
    try {
      // Save changes first
      await apiClient.put(`/expenses/${id}`, {
        expenseDate,
        currency,
        subTotal: parseFloat(subTotal) || 0,
        vATAmount: parseFloat(vatAmount) || 0,
        totalAmount: parseFloat(totalAmount) || 0,
        departmentId,
        gLAccountId: glAccountId || null,
        projectId: projectId || null,
        costCentre: costCentre || null,
        purpose,
        category: isMileage ? 'Mileage' : category,
        notes
      });

      if (uploadQueue.length > 0) {
        await uploadPendingDocuments(id);
      }

      // Submit
      await apiClient.post(`/expenses/${id}/submit`);
      alert('Expense submitted for approval!');
      navigate(`/expenses/${id}`);
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.response?.data?.message || 'Failed to submit expense');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== STATUS BADGE ====================
  const getStatusBadge = (status) => {
    const styles = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Approval': 'bg-yellow-100 text-yellow-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading expense...</p>
        </div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">Expense not found</h3>
        <Link to="/expenses" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
          ← Back to Expenses
        </Link>
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/expenses/${id}`}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Expense Details
        </Link>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Edit className="w-7 h-7 mr-3 text-blue-600" />
              Edit Expense — {expense.ExpenseNumber}
            </h1>
            <p className="text-gray-500 mt-1">
              {isMileage ? 'Mileage Claim' : 'General Expense'} •{' '}
              {getStatusBadge(expense.Status)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || submitting}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {(expense.Status === 'Draft' || expense.Status === 'Rejected') && (
              <button
                onClick={handleSubmitForApproval}
                disabled={saving || submitting}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? 'Submitting...' : 'Submit for Approval'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Rejected Warning */}
      {expense.Status === 'Rejected' && expense.RejectionReason && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-800">This expense was rejected</p>
            <p className="text-sm text-red-700 mt-1">{expense.RejectionReason}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose / Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g., Client meeting lunch, Business trip"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {!isMileage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat.ExpenseCategoryID} value={cat.CategoryName}>
                        {cat.CategoryName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="2"
                />
              </div>
            </div>
          </div>

          {/* Financial Details (General only) */}
          {!isMileage && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="GBP">GBP - British Pound</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    {currencies.filter(c => !['GBP', 'USD', 'EUR'].includes(c.CurrencyCode)).map(curr => (
                      <option key={curr.CurrencyCode} value={curr.CurrencyCode}>
                        {curr.CurrencyCode} - {curr.CurrencyName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Net Amount <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    value={subTotal}
                    onChange={(e) => setSubTotal(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={vatAmount}
                    onChange={(e) => setVatAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={totalAmount}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-semibold"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Mileage Trips */}
          {isMileage && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Car className="h-5 w-5 mr-2 text-blue-600" />
                  Mileage Trips ({trips.length})
                </h2>
                {!showTripForm && (
                  <button
                    type="button"
                    onClick={() => setShowTripForm(true)}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Trip
                  </button>
                )}
              </div>

              {/* Trip Form */}
              {showTripForm && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 space-y-4">
                  <h3 className="font-medium text-gray-900">
                    {editingTripIndex !== null ? 'Edit Trip' : 'New Trip'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                      <input
                        type="date"
                        value={tripDate}
                        onChange={(e) => setTripDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Distance (miles) *</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={distance}
                        onChange={(e) => setDistance(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="25"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
                      <input
                        type="text"
                        value={fromLocation}
                        onChange={(e) => setFromLocation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Home"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
                      <input
                        type="text"
                        value={toLocation}
                        onChange={(e) => setToLocation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Client Office"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Purpose *</label>
                      <input
                        type="text"
                        value={tripPurpose}
                        onChange={(e) => setTripPurpose(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Client meeting"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="editReturnTrip"
                      checked={returnTrip}
                      onChange={(e) => setReturnTrip(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label htmlFor="editReturnTrip" className="text-sm text-gray-700">
                      Return trip (double distance)
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddTrip}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                    >
                      {editingTripIndex !== null ? 'Update Trip' : 'Add Trip'}
                    </button>
                    <button
                      type="button"
                      onClick={resetTripForm}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Trips List */}
              {trips.length > 0 ? (
                <div className="space-y-2">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Miles</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {trips.map((trip, index) => (
                          <tr key={trip.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {trip.date ? new Date(trip.date).toLocaleDateString('en-GB') : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{trip.fromLocation || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{trip.toLocation || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{trip.distance}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">£{trip.ratePerMile}/mi</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              £{trip.amount.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => handleEditTrip(index)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTrip(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4 inline" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-blue-50">
                        <tr>
                          <td colSpan="3" className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                            Total:
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                            {getTotalMiles().toFixed(1)} mi
                          </td>
                          <td></td>
                          <td className="px-4 py-3 text-sm font-semibold text-blue-700">
                            £{getTotalMileageAmount().toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Car className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No trips yet. Click "Add Trip" to get started.</p>
                </div>
              )}
            </div>
          )}

          {/* Allocation */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Allocation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department <span className="text-red-500">*</span>
                </label>
                <select
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select department...</option>
                  {departments.map(dept => (
                    <option key={dept.DepartmentID} value={dept.DepartmentID}>
                      {dept.DepartmentName}
                    </option>
                  ))}
                </select>
              </div>
              {!isMileage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GL Account</label>
                  <select
                    value={glAccountId}
                    onChange={(e) => setGlAccountId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select GL account...</option>
                    {glAccounts.map(gl => (
                      <option key={gl.GLAccountID} value={gl.GLAccountID}>
                        {gl.AccountCode} - {gl.AccountName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project (Optional)</label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select project...</option>
                  {projects.map(proj => (
                    <option key={proj.ProjectID} value={proj.ProjectID}>
                      {proj.ProjectCode} - {proj.ProjectName}
                    </option>
                  ))}
                </select>
              </div>
              {!isMileage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost Centre (Optional)</label>
                  <input
                    type="text"
                    value={costCentre}
                    onChange={(e) => setCostCentre(e.target.value)}
                    placeholder="e.g., CC-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
              {isMileage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                  <select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Car">Car</option>
                    <option value="Motorcycle">Motorcycle</option>
                    <option value="Bicycle">Bicycle</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
            <dl className="space-y-3">
              {isMileage ? (
                <>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Total Miles</dt>
                    <dd className="text-sm font-medium text-gray-900">{getTotalMiles().toFixed(1)} mi</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Trips</dt>
                    <dd className="text-sm font-medium text-gray-900">{trips.length}</dd>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-gray-200">
                    <dt className="text-base font-semibold text-gray-900">Total</dt>
                    <dd className="text-base font-semibold text-blue-600">
                      £{getTotalMileageAmount().toFixed(2)}
                    </dd>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Net Amount</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {formatCurrencyAmount(subTotal || 0, currency)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">VAT</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {formatCurrencyAmount(vatAmount || 0, currency)}
                    </dd>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-gray-200">
                    <dt className="text-base font-semibold text-gray-900">Total</dt>
                    <dd className="text-base font-semibold text-blue-600">
                      {formatCurrencyAmount(totalAmount || 0, currency)}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {/* Existing Documents */}
          {documents.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Existing Documents ({documents.length})
              </h2>
              <div className="space-y-3">
                {documents.map(doc => (
                  <div key={doc.ExpenseDocumentID} className="border border-gray-200 rounded-lg p-3">
                    {doc.BlobURL && (doc.MimeType?.startsWith('image/') || doc.FileName?.match(/\.(jpg|jpeg|png|gif)$/i)) && (
                      <img
                        src={doc.BlobURL}
                        alt={doc.FileName}
                        className="w-full h-32 object-contain bg-gray-50 rounded mb-2"
                      />
                    )}
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.FileName}</p>
                    <p className="text-xs text-gray-500">
                      Uploaded {new Date(doc.UploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload New Documents */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Upload className="w-5 h-5 mr-2" />
              Upload Receipts
            </h2>
            <label className="block w-full cursor-pointer">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <div className="text-sm text-gray-600">Click to upload receipts</div>
                <div className="text-xs text-gray-500 mt-1">PDF, JPG, PNG up to 10MB</div>
              </div>
              <input
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                className="hidden"
              />
            </label>

            {uploadQueue.length > 0 && (
              <div className="mt-3 space-y-2">
                {uploadQueue.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{doc.name}</div>
                      <div className="text-xs text-gray-500">{(doc.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <button
                      onClick={() => handleRemoveNewDoc(index)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions (Mobile) */}
          <div className="bg-white shadow rounded-lg p-6 lg:hidden space-y-3">
            <button
              onClick={handleSave}
              disabled={saving || submitting}
              className="w-full inline-flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {(expense.Status === 'Draft' || expense.Status === 'Rejected') && (
              <button
                onClick={handleSubmitForApproval}
                disabled={saving || submitting}
                className="w-full inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? 'Submitting...' : 'Submit for Approval'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
